<script setup>
import { ref, watch } from 'vue'
import { createAuthUser } from '@core/services/users.js'
import { normalizePhoneNumber } from '@core/utils/formatting.js'
import ClientLinkModal from '../ClientLinkModal.vue'

const props = defineProps({
  open: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'created'])

// ============================================
// State
// ============================================
const createForm = ref({ email: '', password: '', displayName: '', phoneNumber: '', role: null })
const createLoading = ref(false)
const createError = ref(null)
const createSuccess = ref(null)
const showTempPassword = ref(false)
const tempPasswordValue = ref('')
const showPassword = ref(false)

// Client linking
const createClientLinkModalOpen = ref(false)
const createSelectedClient = ref(null)

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'client', label: 'Client' },
  { value: 'agent', label: 'Agent' },
  { value: null, label: 'None' },
]

// ============================================
// Watchers
// ============================================

// Auto-remove leading '0' in phone
watch(
  () => createForm.value.phoneNumber,
  (newPhone) => {
    if (!newPhone) return
    let cleaned = newPhone.replace(/[\s-]/g, '')
    if (cleaned.startsWith('0')) {
      createForm.value.phoneNumber = cleaned.substring(1)
    }
  }
)

// Clear client if role changes from 'client'
watch(
  () => createForm.value.role,
  (newRole, oldRole) => {
    if (oldRole === 'client' && newRole !== 'client') {
      createSelectedClient.value = null
    }
  }
)

// Reset form when modal opens
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      createForm.value = { email: '', password: '', displayName: '', phoneNumber: '', role: null }
      createSelectedClient.value = null
      createError.value = null
      createSuccess.value = null
      createLoading.value = false
      showTempPassword.value = false
      tempPasswordValue.value = ''
    }
  }
)

// ============================================
// Actions
// ============================================
function closeModal() {
  createClientLinkModalOpen.value = false
  createSelectedClient.value = null
  emit('close')
}

function handleCreateClientLinked(clientData) {
  createSelectedClient.value = { makat: clientData.makat, name: clientData.name }
  createClientLinkModalOpen.value = false
}

async function submitCreateUser() {
  if (!createForm.value.email) {
    createError.value = 'אימייל הוא שדה חובה'
    return
  }
  if (createForm.value.role === 'client' && !createSelectedClient.value) {
    createError.value = 'חובה לשייך לקוח עבור משתמש בתפקיד Client'
    return
  }

  createLoading.value = true
  createError.value = null
  createSuccess.value = null

  try {
    const payload = { email: createForm.value.email }
    if (createForm.value.password) payload.password = createForm.value.password
    if (createForm.value.displayName) payload.displayName = createForm.value.displayName
    if (createForm.value.phoneNumber) {
      payload.phoneNumber = normalizePhoneNumber(createForm.value.phoneNumber)
    }
    if (createForm.value.role !== undefined) payload.role = createForm.value.role
    if (createForm.value.role === 'client' && createSelectedClient.value) {
      payload.client_makat = createSelectedClient.value.makat
    }

    const result = await createAuthUser(payload)
    createSuccess.value = 'המשתמש נוצר בהצלחה!'

    if (result.tempPassword) {
      tempPasswordValue.value = result.tempPassword
      showTempPassword.value = true
    }

    emit('created')
  } catch (err) {
    console.error('Failed to create user:', err)
    createError.value = err.message || 'שגיאה ביצירת משתמש'
  } finally {
    createLoading.value = false
  }
}

function copyTempPassword() {
  navigator.clipboard.writeText(tempPasswordValue.value)
  alert('הסיסמה הועתקה!')
}
</script>

<template>
  <Transition name="modal">
    <div v-if="open" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <div class="modal-header">
          <h2 class="modal-title">יצירת משתמש חדש</h2>
          <button class="modal-close" @click="closeModal">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="createError" class="msg msg-error">{{ createError }}</div>
          <div v-if="createSuccess" class="msg msg-success">{{ createSuccess }}</div>

          <!-- Temp Password Display -->
          <div v-if="showTempPassword" class="temp-password-box">
            <span class="temp-label">סיסמה זמנית:</span>
            <div class="temp-password-row">
              <code class="temp-password">{{ tempPasswordValue }}</code>
              <button class="btn-copy" @click="copyTempPassword">העתק</button>
            </div>
            <p class="temp-note">שמור סיסמה זו! לא ניתן לצפות בה שוב.</p>
          </div>

          <div v-if="!showTempPassword">
            <div class="form-group">
              <label class="form-label">אימייל *</label>
              <input
                v-model="createForm.email"
                type="email"
                class="form-input"
                placeholder="user@example.com"
                required
              />
            </div>
            <div class="form-group">
              <label class="form-label">סיסמה (אופציונלי)</label>
              <div class="password-input-wrapper">
                <input
                  v-model="createForm.password"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-input password-input"
                  placeholder="השאר ריק ליצירת סיסמה זמנית"
                />
                <button
                  type="button"
                  class="btn-toggle-password"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <template v-if="!showPassword">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </template>
                    <template v-else>
                      <path
                        d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"
                      />
                      <path
                        d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"
                      />
                      <path d="M14.12 14.12a3 3 0 11-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </template>
                  </svg>
                </button>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">שם</label>
              <input
                v-model="createForm.displayName"
                type="text"
                class="form-input"
                placeholder="שם מלא"
              />
            </div>
            <div class="form-group">
              <label class="form-label">טלפון</label>
              <p class="form-hint">* ללא ה-0 הראשון במספר</p>
              <div class="phone-input-wrapper">
                <input
                  v-model="createForm.phoneNumber"
                  type="text"
                  class="form-input phone-input-with-prefix"
                  placeholder="544221254"
                />
                <span class="phone-prefix">+972</span>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">תפקיד</label>
              <select v-model="createForm.role" class="form-input">
                <option v-for="opt in roleOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>

            <!-- Client Linking Section -->
            <div v-if="createForm.role === 'client'" class="form-group client-link-section">
              <label class="form-label">שיוך לקוח *</label>
              <div v-if="createSelectedClient" class="client-link-display">
                <div class="linked-client-info">
                  <span class="client-makat">{{ createSelectedClient.makat }}</span>
                  <span class="client-name">{{ createSelectedClient.name }}</span>
                </div>
                <button
                  type="button"
                  class="btn-change-client"
                  @click="createClientLinkModalOpen = true"
                >
                  שנה
                </button>
              </div>
              <button
                v-else
                type="button"
                class="btn-link-client"
                @click="createClientLinkModalOpen = true"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                </svg>
                <span>שייך לקוח</span>
              </button>
              <p class="help-text">חובה לשייך לקוח עבור משתמש בתפקיד Client</p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" @click="closeModal">ביטול</button>
          <button
            v-if="!showTempPassword"
            class="btn-create"
            :disabled="createLoading"
            @click="submitCreateUser"
          >
            {{ createLoading ? 'יוצר...' : 'צור משתמש' }}
          </button>
          <button v-else class="btn-create" @click="closeModal">סגור</button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Client Link Modal (Create User) -->
  <ClientLinkModal
    :open="createClientLinkModalOpen"
    :user-uid="''"
    :current-client-makat="createSelectedClient?.makat || null"
    :current-client-name="createSelectedClient?.name || null"
    @close="createClientLinkModalOpen = false"
    @linked="handleCreateClientLinked"
  />
</template>

<style scoped>
/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
}

.modal {
  width: 100%;
  max-width: 480px;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--gradient-header);
  color: white;
}

.modal-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin: 0;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: var(--spacing-lg);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
}

/* Messages */
.msg {
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-md);
}

.msg-error {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.msg-success {
  background: rgba(16, 185, 129, 0.1);
  color: var(--color-success);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

/* Form */
.form-group {
  margin-bottom: var(--spacing-md);
}

.form-label {
  display: block;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xs);
}

.form-hint {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-xs) 0;
  font-style: italic;
  opacity: 0.8;
}

.form-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  transition: all var(--transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

/* Password input with toggle */
.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input {
  padding-left: 40px;
}

.btn-toggle-password {
  position: absolute;
  left: var(--spacing-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0;
  transition: color var(--transition-fast);
}

.btn-toggle-password:hover {
  color: var(--text-secondary);
}

/* Phone input with prefix */
.phone-input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  transition: all var(--transition-fast);
}

.phone-input-wrapper:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.phone-prefix {
  padding: var(--spacing-sm) var(--spacing-md) var(--spacing-sm) 0;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  border-left: 1px solid var(--border-color);
  user-select: none;
  white-space: nowrap;
  direction: ltr;
  text-align: left;
}

.phone-input-with-prefix {
  border: none !important;
  box-shadow: none !important;
  flex: 1;
  min-width: 0;
}

.phone-input-with-prefix:focus {
  outline: none;
  box-shadow: none !important;
}

/* Buttons */
.btn-cancel {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-cancel:hover {
  background: var(--bg-tertiary);
}

.btn-create {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--gradient-primary);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-create:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn-create:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Temp password */
.temp-password-box {
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-md);
}

.temp-label {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-warning);
  margin-bottom: var(--spacing-sm);
}

.temp-password-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.temp-password {
  flex: 1;
  padding: var(--spacing-sm);
  background: var(--bg-primary);
  border-radius: var(--border-radius-sm);
  font-family: monospace;
  font-size: var(--font-size-base);
  color: var(--text-primary);
}

.btn-copy {
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-warning);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
}

.temp-note {
  margin-top: var(--spacing-sm);
  font-size: var(--font-size-xs);
  color: var(--color-warning);
}

/* Client link section */
.client-link-section {
  border: 2px dashed var(--color-primary, #3b82f6);
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  background: rgba(59, 130, 246, 0.02);
}

.client-link-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
}

.linked-client-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.client-makat {
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  font-size: var(--font-size-base);
}

.client-name {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.btn-link-client,
.btn-change-client {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 8px 16px;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-change-client {
  background: transparent;
  color: var(--color-primary);
  border: 1px solid var(--color-primary);
}

.btn-link-client:hover,
.btn-change-client:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.help-text {
  margin-top: var(--spacing-xs);
  margin-bottom: 0;
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  font-style: italic;
}

/* Modal Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: scale(0.95);
}
</style>
